import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { CheckCircle, FileText, Shield, Eraser, Eye, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SignerContratUniverselPage() {
  const { token } = useParams();
  const [contrat, setContrat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nomSignataire, setNomSignataire] = useState('');
  const [qualite, setQualite] = useState('');
  const [rolePartie, setRolePartie] = useState('client');
  const [accepte, setAccepte] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [driveLink, setDriveLink] = useState(null);
  const [hasSignature, setHasSignature] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/contrats-universels/public/sign/${token}`);
        if (!r.ok) throw new Error('Lien invalide');
        const d = await r.json();
        setContrat(d);
        if (d.statut === 'signe') setSigned(true);
        // Préselection du rôle partie
        const partyClient = (d.parties || []).find(p =>
          ['client', 'acheteur', 'intermediaire'].includes(p.role)
        );
        if (partyClient) setRolePartie(partyClient.role);
      } catch (e) {
        toast.error("Lien invalide ou expiré");
      } finally { setLoading(false); }
    })();
  }, [token]);

  // Attache des listeners NATIFS avec { passive: false } pour que preventDefault()
  // fonctionne sur les anciens mobiles (iOS Safari < 13, vieux Android WebView).
  // Sans ça, React traite les touch events en mode passif → scroll page + impossible de signer.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || signed) return;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    let drawing = false;

    const getPos = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
    };

    const start = (cx, cy) => {
      drawing = true;
      const { x, y } = getPos(cx, cy);
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const move = (cx, cy) => {
      if (!drawing) return;
      const { x, y } = getPos(cx, cy);
      ctx.lineTo(x, y);
      ctx.stroke();
      setHasSignature(true);
    };
    const end = () => { drawing = false; };

    const onMouseDown = (e) => { e.preventDefault(); start(e.clientX, e.clientY); };
    const onMouseMove = (e) => { if (drawing) { e.preventDefault(); move(e.clientX, e.clientY); } };
    const onMouseUp = () => end();

    const onTouchStart = (e) => {
      if (!e.touches || e.touches.length === 0) return;
      e.preventDefault();
      const t = e.touches[0];
      start(t.clientX, t.clientY);
    };
    const onTouchMove = (e) => {
      if (!drawing || !e.touches || e.touches.length === 0) return;
      e.preventDefault();
      const t = e.touches[0];
      move(t.clientX, t.clientY);
    };
    const onTouchEnd = (e) => { e.preventDefault(); end(); };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mouseleave', onMouseUp);
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', onTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('mouseleave', onMouseUp);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
      canvas.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [loading, signed]);

  const clearSign = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSign = async () => {
    if (!nomSignataire.trim()) { toast.error('Indique ton nom'); return; }
    if (!accepte) { toast.error('Accepte les conditions du contrat'); return; }
    if (!hasSignature) { toast.error('Signe dans le cadre avec ta souris ou ton doigt'); return; }
    setSigning(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const r = await fetch(`${API}/api/contrats-universels/public/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom_signataire: nomSignataire,
          qualite,
          role_partie: rolePartie,
          image_base64: dataUrl,
          accept_conditions: true,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur signature');
      }
      const result = await r.json();
      setSigned(true);
      setDriveLink(result.drive_link);
      toast.success('Contrat signé et archivé !');
    } catch (e) { toast.error(e.message); }
    finally { setSigning(false); }
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Chargement...</div>;
  if (!contrat) return <div className="p-10 text-center text-red-600">Contrat introuvable ou lien expiré.</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-10" data-testid="sign-contrat-u-page">
          <div className="flex items-start gap-3 mb-6 pb-6 border-b-2 border-emerald-600">
            <Shield className="h-10 w-10 text-emerald-700 flex-shrink-0 mt-1" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-emerald-800">{contrat.titre || 'Contrat'}</h1>
              <p className="text-sm text-slate-600 mt-1">
                {contrat.type_contrat === 'tripartite' ? 'Contrat tripartite' : 'Contrat de prestations'} ·
                {(contrat.parties || []).length} parties
              </p>
            </div>
          </div>

          {signed ? (
            <div className="text-center py-12" data-testid="sign-success">
              <CheckCircle className="h-24 w-24 text-emerald-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-emerald-800 mb-2">Contrat signé !</h2>
              <p className="text-slate-600 mb-6">
                Merci. Une copie signée a été envoyée par email et archivée automatiquement dans Google Drive.
              </p>
              {driveLink && (
                <a href={driveLink} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 bg-emerald-700 text-white px-6 py-3 rounded-lg hover:bg-emerald-800">
                  <ExternalLink className="w-4 h-4" /> Ouvrir le PDF signé dans Drive
                </a>
              )}
              <div className="mt-4">
                <a href={`${API}/api/contrats-universels/public/sign/${token}/pdf`}
                   target="_blank" rel="noopener noreferrer"
                   className="text-emerald-700 hover:underline text-sm">
                  Télécharger le PDF signé
                </a>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Résumé */}
              <div className="bg-emerald-50 rounded-lg p-4">
                <h3 className="font-semibold text-emerald-900 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Contenu du contrat
                </h3>
                <div className="text-sm space-y-1">
                  <div>Durée : {contrat.date_debut} → {contrat.date_fin}</div>
                  {contrat.cultures?.length > 0 && (
                    <div>{contrat.cultures.length} culture(s), surface totale {contrat.cultures.reduce((s, c) => s + (c.surface_ha || 0), 0)} ha</div>
                  )}
                  {contrat.prestations?.length > 0 && <div>{contrat.prestations.length} prestation(s)</div>}
                  {contrat.prix_vente?.length > 0 && <div>{contrat.prix_vente.length} flux de vente</div>}
                  {contrat.inclure_annexe_bio && <div className="text-emerald-700 font-semibold">✓ Annexe BIO (CDC AB)</div>}
                </div>
                <a href={`${API}/api/contrats-universels/public/sign/${token}/pdf`}
                   target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 text-emerald-700 hover:underline text-sm mt-3"
                   data-testid="view-pdf-btn">
                  <Eye className="w-4 h-4" /> Voir le PDF intégral
                </a>
              </div>

              {/* Formulaire signataire */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600">Nom complet *</label>
                  <Input value={nomSignataire} onChange={e => setNomSignataire(e.target.value)}
                         placeholder="Prénom Nom" data-testid="nom-signataire" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Qualité / Fonction</label>
                  <Input value={qualite} onChange={e => setQualite(e.target.value)}
                         placeholder="Gérant, Directeur, etc." data-testid="qualite" />
                </div>
              </div>

              {contrat.parties?.length > 1 && (
                <div>
                  <label className="text-xs font-semibold text-slate-600">Je signe en tant que *</label>
                  <select value={rolePartie} onChange={e => setRolePartie(e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded" data-testid="role-partie">
                    {contrat.parties.map(p => (
                      <option key={p.role} value={p.role}>
                        Le {p.role.charAt(0).toUpperCase() + p.role.slice(1)} — {p.nom}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Pad signature */}
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-2 flex items-center justify-between">
                  <span>Signature manuscrite *</span>
                  <button type="button" onClick={clearSign}
                          className="text-xs text-red-600 hover:underline flex items-center gap-1">
                    <Eraser className="w-3 h-3" /> Effacer
                  </button>
                </label>
                <canvas
                  ref={canvasRef}
                  width={900}
                  height={220}
                  style={{ touchAction: 'none', WebkitTapHighlightColor: 'transparent', WebkitUserSelect: 'none', userSelect: 'none' }}
                  className="border-2 border-dashed border-emerald-400 rounded-lg w-full h-[160px] sm:h-[200px] bg-white cursor-crosshair touch-none select-none"
                  data-testid="signature-pad"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Dessine ta signature avec la souris ou le doigt (mobile). Cette signature a valeur juridique (eIDAS).
                </p>
              </div>

              {/* Acceptation */}
              <label className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer"
                     data-testid="accepte-conditions">
                <Checkbox checked={accepte} onCheckedChange={setAccepte} />
                <span className="text-sm text-slate-700">
                  Je reconnais avoir pris connaissance de l'intégralité du contrat et de ses annexes.
                  J'accepte l'ensemble des clauses sans réserve et reconnais la validité juridique
                  de ma signature électronique.
                </span>
              </label>

              <Button
                onClick={handleSign}
                disabled={signing || !nomSignataire || !accepte || !hasSignature}
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white text-base py-6"
                data-testid="signer-btn"
              >
                {signing ? 'Signature en cours...' : '✍️ Signer le contrat'}
              </Button>

              <p className="text-xs text-slate-400 text-center">
                Signature conforme au règlement eIDAS (niveau simple). IP, horodatage et User Agent seront enregistrés.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
